import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './styles.css'

import { initSentry } from './lib/sentry'

import { AuthProvider } from './context/AuthContext'
import { RequireAuth } from './components/RequireAuth'
import { AppLayout } from './components/AppLayout'
import { ProviderOnboardingGate } from './components/ProviderOnboardingGate'
import { RequireMfaEnrolled } from './components/RequireMfaEnrolled'

import PublicLayout from './pages/public/PublicLayout'
import Home from './pages/public/Home'
import Platform from './pages/public/Platform'
import HowItWorks from './pages/public/HowItWorks'
import SolutionsHealthPlans from './pages/public/solutions/HealthPlans'
import SolutionsProviderOrganizations from './pages/public/solutions/ProviderOrganizations'
import SolutionsPractices from './pages/public/solutions/Practices'
import Pricing from './pages/public/Pricing'
import Security from './pages/public/Security'
import Resources from './pages/public/Resources'
import About from './pages/public/About'
import Contact from './pages/public/Contact'
import Terms from './pages/public/Terms'
import Privacy from './pages/public/Privacy'
import Cookies from './pages/public/Cookies'
import Login from './pages/public/Login'
import Signup from './pages/public/Signup'
import SignupChoice from './pages/public/SignupChoice'
import ProviderSignup from './pages/public/ProviderSignup'
import FindCare from './pages/public/FindCare'
import ResetPasswordRequest from './pages/public/ResetPasswordRequest'
import ResetPasswordConfirm from './pages/public/ResetPasswordConfirm'
import VerifyEmailPage from './pages/public/VerifyEmailPage'
import InviteAcceptPage from './pages/public/InviteAcceptPage'

import OrgDashboardRouter from './pages/practice/OrgDashboardRouter'
import ProviderDashboard from './pages/practice/ProviderDashboard'
import ProviderOnboardingWizard from './pages/provider/ProviderOnboardingWizard'

import PatientIntakeWizard from './pages/patient/PatientIntakeWizard'
import PatientMatchesPage from './pages/patient/PatientMatchesPage'
import PatientDashboard from './pages/patient/PatientDashboard'
import ProviderProfile from './pages/patient/ProviderProfile'
import BookAppointment from './pages/patient/BookAppointment'
import AccountSettings from './pages/shared/AccountSettings'

import AdminActionCenter from './pages/admin/AdminActionCenter'
import AdminOverview from './pages/admin/AdminOverview'
import AdminOrganizations from './pages/admin/AdminOrganizations'
import AdminOrgDetail from './pages/admin/AdminOrgDetail'
import AdminProviders from './pages/admin/AdminProviders'
import AdminProviderDetail from './pages/admin/AdminProviderDetail'
import AdminPatients from './pages/admin/AdminPatients'
import AdminPatientDetail from './pages/admin/AdminPatientDetail'
import AdminMatching from './pages/admin/AdminMatching'
import AdminContinuity from './pages/admin/AdminContinuity'
import AdminBillingLedger from './pages/admin/AdminBillingLedger'
import AdminEventLogs from './pages/admin/AdminEventLogs'
import AdminCredentialingQueue from './pages/admin/AdminCredentialingQueue'
import AdminContactRequests from './pages/admin/AdminContactRequests'
import AdminSystemHealth from './pages/admin/AdminSystemHealth'
import AdminPayers from './pages/admin/AdminPayers'
import { AdminGlobalSearch } from './components/AdminGlobalSearch'

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/solutions/health-plans" element={<SolutionsHealthPlans />} />
            <Route path="/solutions/provider-organizations" element={<SolutionsProviderOrganizations />} />
            <Route path="/solutions/practices" element={<SolutionsPractices />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/security" element={<Security />} />
            <Route path="/resources" element={<Resources />} />
            {/* Renamed as part of the enterprise positioning rewrite — kept as
                client-side redirects (not deleted routes) so bookmarks/backlinks
                to the old marketing URLs still land somewhere real, both in
                local dev and in the deployed build (see also the matching
                301s in firebase.json for the deployed case). */}
            <Route path="/product" element={<Navigate to="/platform" replace />} />
            <Route path="/for-practices" element={<Navigate to="/solutions/practices" replace />} />
            <Route path="/for-providers" element={<Navigate to="/solutions/practices" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignupChoice />} />
            <Route path="/signup/practice" element={<Signup />} />
            <Route path="/signup/provider" element={<ProviderSignup />} />
            <Route path="/find-care" element={<FindCare />} />
          </Route>

          <Route path="/reset-password/request" element={<ResetPasswordRequest />} />
          <Route path="/reset-password/:token" element={<ResetPasswordConfirm />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          <Route
            path="/practice"
            element={
              <RequireAuth roles={['practice_owner', 'practice_admin']}>
                <AppLayout orgLabel="Practice" navItems={[{ to: '/practice', label: 'Continuity queue' }]} />
              </RequireAuth>
            }
          >
            <Route index element={<OrgDashboardRouter />} />
          </Route>

          <Route
            path="/practice/provider"
            element={
              <RequireAuth roles={['provider']}>
                <AppLayout orgLabel="Provider" navItems={[{ to: '/practice/provider', label: "Today's queue" }]} />
              </RequireAuth>
            }
          >
            <Route path="onboarding" element={<ProviderOnboardingWizard />} />
            <Route element={<ProviderOnboardingGate />}>
              <Route index element={<ProviderDashboard />} />
            </Route>
          </Route>

          <Route
            path="/patient"
            element={
              <RequireAuth roles={['patient']}>
                <AppLayout
                  orgLabel="Patient"
                  navItems={[
                    { to: '/patient', label: 'My care' },
                    { to: '/patient/intake', label: 'Intake' },
                    { to: '/patient/matches', label: 'Matches' },
                  ]}
                />
              </RequireAuth>
            }
          >
            <Route index element={<PatientDashboard />} />
            <Route path="intake" element={<PatientIntakeWizard />} />
            <Route path="matches" element={<PatientMatchesPage />} />
            <Route path="providers/:providerId" element={<ProviderProfile />} />
            <Route path="book/:providerId" element={<BookAppointment />} />
          </Route>

          <Route
            path="/admin"
            element={
              <RequireAuth roles={['super_admin', 'atuune_admin']}>
                <AppLayout
                  orgLabel="ATUUNE Internal"
                  topBarExtra={<AdminGlobalSearch />}
                  navItems={[
                    { to: '/admin/action-center', label: 'Action Center' },
                    { to: '/admin/overview', label: 'Overview' },
                    { to: '/admin/organizations', label: 'Organizations' },
                    { to: '/admin/providers', label: 'Providers' },
                    { to: '/admin/patients', label: 'Patients' },
                    { to: '/admin/matching', label: 'Matching activity' },
                    { to: '/admin/continuity', label: 'Continuity' },
                    { to: '/admin/billing', label: 'Billing ledger' },
                    { to: '/admin/credentialing', label: 'Credentialing' },
                    { to: '/admin/contact-requests', label: 'Inbound requests' },
                    { to: '/admin/event-logs', label: 'Event logs' },
                    { to: '/admin/system-health', label: 'System health' },
                    { to: '/admin/payers', label: 'Payers' },
                  ]}
                />
              </RequireAuth>
            }
          >
            {/* Mandatory MFA: the backend already 403s every /admin/* API
                call for an unenrolled super_admin/atuune_admin (see
                get_current_user's enrollment gate) — this wraps every child
                route here so the UI redirects to Account Settings instead of
                rendering a page that would just fail to load anything. */}
            <Route element={<RequireMfaEnrolled />}>
              <Route index element={<Navigate to="action-center" replace />} />
              <Route path="action-center" element={<AdminActionCenter />} />
              <Route path="overview" element={<AdminOverview />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="organizations/:orgId" element={<AdminOrgDetail />} />
              <Route path="providers" element={<AdminProviders />} />
              <Route path="providers/:providerId" element={<AdminProviderDetail />} />
              <Route path="patients" element={<AdminPatients />} />
              <Route path="patients/:patientId" element={<AdminPatientDetail />} />
              <Route path="matching" element={<AdminMatching />} />
              <Route path="continuity" element={<AdminContinuity />} />
              <Route path="billing" element={<AdminBillingLedger />} />
              <Route path="credentialing" element={<AdminCredentialingQueue />} />
              <Route path="contact-requests" element={<AdminContactRequests />} />
              <Route path="event-logs" element={<AdminEventLogs />} />
              <Route path="system-health" element={<AdminSystemHealth />} />
              <Route path="payers" element={<AdminPayers />} />
            </Route>
          </Route>

          <Route
            path="/account"
            element={
              <RequireAuth>
                <AppLayout orgLabel="Account" navItems={[]} />
              </RequireAuth>
            }
          >
            <Route index element={<AccountSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}

initSentry()
createRoot(document.getElementById('root')!).render(<App />)
